import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Link2, Unlink, ExternalLink, CheckCircle, AlertCircle, Clock, RefreshCw, Shield } from 'lucide-react';
import { getConnectedAccounts, connectGoogle, disconnectAccount, getGoogleProperties, saveAccountSettings, getAdsAccounts, saveAdsCustomer } from '../services/integrationsService';

const PROVIDER_CONFIG = {
  google: {
    name: 'Google',
    description: 'Connect Google Analytics, Search Console, and Google Ads',
    icon: (
      <svg viewBox="0 0 24 24" className="w-8 h-8" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
    services: ['Google Analytics (GA4)', 'Google Search Console', 'Google Ads'],
    dataLimits: {
      'Google Analytics': 'Data available from property creation date',
      'Google Search Console': 'Up to 16 months of search data',
      'Google Ads': 'Full historical data from account creation'
    }
  }
};

const STATUS_STYLES = {
  active: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle },
  expired: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Clock },
  revoked: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertCircle },
  error: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertCircle }
};

export default function IntegrationsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [notification, setNotification] = useState(null);
    const [propertyData, setPropertyData] = useState({});
    const [selectedSettings, setSelectedSettings] = useState({});
    const [loadingProperties, setLoadingProperties] = useState({});
    const [savingSettings, setSavingSettings] = useState({});
  const [adsData, setAdsData] = useState({});
  const [savingAds, setSavingAds] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await getConnectedAccounts();
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Load properties when accounts are loaded
  useEffect(() => {
    if (accounts.length > 0) {
      accounts.forEach(acct => {
        if (acct.provider === 'google' && acct.status === 'active') {
          loadPropertiesForAccount(acct.id);
        }
      });
    }
  }, [accounts]);

  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const email = searchParams.get('email');
    const provider = searchParams.get('provider');

    if (success === 'connected') {
      setNotification({ type: 'success', message: `Successfully connected ${provider || 'account'}${email ? ' (' + email + ')' : ''}` });
      fetchAccounts();
      setSearchParams({});
    } else if (error) {
      setNotification({ type: 'error', message: `Connection failed: ${decodeURIComponent(error)}` });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, fetchAccounts]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

    const getCurrentDomain = () => {
        try {
            const domainData = localStorage.getItem('selectedDomain');
            if (domainData) {
            // selectedDomain is stored as a plain string
            return domainData;
            }
        } catch (e) {}
        return null;
    };

    const loadPropertiesForAccount = async (accountId) => {
        setLoadingProperties(prev => ({ ...prev, [accountId]: true }));
        try {
            const res = await getGoogleProperties(accountId);
            const props = res.data?.properties || [];
            const sites = res.data?.searchConsoleSites || [];
            setPropertyData(prev => ({ ...prev, [accountId]: { properties: props, sites: sites } }));

      // Load Google Ads accessible customers for this connection
      try {
        const adsRes = await getAdsAccounts(accountId);
        setAdsData(prev => ({ ...prev, [accountId]: { customers: adsRes.data?.customers || [] } }));
      } catch (adsErr) {
        console.error('Failed to load Ads accounts for', accountId, adsErr);
        setAdsData(prev => ({ ...prev, [accountId]: { customers: [], error: true } }));
      }

      // Restore saved adsCustomerId from account metadata (account-level)
      try {
        const acctA = accounts.find(a => a.id === accountId);
        if (acctA?.metadata) {
          const metaA = typeof acctA.metadata === 'string' ? JSON.parse(acctA.metadata) : acctA.metadata;
          if (metaA?.adsCustomerId) {
            setSelectedSettings(prev => ({
              ...prev,
              [accountId]: { ...(prev[accountId] || {}), adsCustomerId: metaA.adsCustomerId }
            }));
          }
        }
      } catch (e) { /* ignore metadata parse errors */ }
            
            // Check if there are saved settings in account metadata
            const acct = accounts.find(a => a.id === accountId);
            if (acct?.metadata) {
                try {
                    const meta = typeof acct.metadata === 'string' ? JSON.parse(acct.metadata) : acct.metadata;
                    const domain = getCurrentDomain();
                    if (domain && meta?.domainSettings?.[domain]) {
                        const saved = meta.domainSettings[domain];
                        setSelectedSettings(prev => ({
                            ...prev,
                            [accountId]: {
                                ga4PropertyId: saved.ga4PropertyId || '',
                                gscSiteUrl: saved.gscSiteUrl || ''
                            }
                        }));
                    }
                } catch (e) {}
            }
        } catch (err) {
            console.error('Failed to load properties for account', accountId, err);
            // Still show the selector section with empty lists so user knows the feature exists
            setPropertyData(prev => ({ ...prev, [accountId]: { properties: [], sites: [], error: true } }));
        } finally {
            setLoadingProperties(prev => ({ ...prev, [accountId]: false }));
        }
    };

    const handleSettingChange = (accountId, field, value) => {
        setSelectedSettings(prev => ({
            ...prev,
            [accountId]: {
                ...(prev[accountId] || {}),
                [field]: value
            }
        }));
    };

    const handleSaveSettings = async (accountId) => {
        const domain = getCurrentDomain();
        if (!domain) {
            setNotification({ type: 'error', message: 'No domain selected. Please select a domain first.' });
            return;
        }
        
        const settings = selectedSettings[accountId] || {};
        const propData = propertyData[accountId] || {};
        const selectedProp = (propData.properties || []).find(p => p.propertyId === settings.ga4PropertyId);
        
        setSavingSettings(prev => ({ ...prev, [accountId]: true }));
        try {
            await saveAccountSettings(accountId, {
                domain,
                ga4PropertyId: settings.ga4PropertyId || null,
                ga4PropertyName: selectedProp?.displayName || null,
                gscSiteUrl: settings.gscSiteUrl || null
            });
            setNotification({ type: 'success', message: `Property settings saved for ${domain}` });
            fetchAccounts();
        } catch (err) {
            setNotification({ type: 'error', message: 'Failed to save settings. Please try again.' });
        } finally {
            setSavingSettings(prev => ({ ...prev, [accountId]: false }));
        }
    };

    const handleSaveAds = async (accountId) => {
    const settings = selectedSettings[accountId] || {};
    const adsCustomerId = (settings.adsCustomerId || '').replace(/\D/g, '');
    if (!adsCustomerId) {
      setNotification({ type: 'error', message: 'Please select a Google Ads account first.' });
      return;
    }
    setSavingAds(prev => ({ ...prev, [accountId]: true }));
    try {
      await saveAdsCustomer(accountId, adsCustomerId);
      setNotification({ type: 'success', message: 'Google Ads account saved.' });
    } catch (err) {
      console.error('Failed to save Ads account', err);
      setNotification({ type: 'error', message: 'Failed to save Google Ads account.' });
    } finally {
      setSavingAds(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const handleConnect = async (provider) => {
        setConnecting(provider);
        try {
            const res = await connectGoogle();
            if (res.data.authUrl) {
                window.location.href = res.data.authUrl;
                return;
            }
            setNotification({ type: 'error', message: 'Failed to get authorization URL. Please try again.' });
        } catch (err) {
            setNotification({ type: 'error', message: 'Failed to initiate connection. Please try again.' });
        } finally {
            setConnecting(null);
        }
    };

  const handleDisconnect = async (accountId, email) => {
    if (!window.confirm(`Are you sure you want to disconnect ${email}? You will need to reconnect to use this account in reports.`)) return;
    setDisconnecting(accountId);
    try {
      await disconnectAccount(accountId);
      setNotification({ type: 'success', message: `Successfully disconnected ${email}` });
      fetchAccounts();
    } catch (err) {
      setNotification({ type: 'error', message: 'Failed to disconnect account.' });
    } finally {
      setDisconnecting(null);
    }
  };

  const googleAccounts = accounts.filter(a => a.provider === 'google');

  return (
    <>
      <Helmet>
        <title>Connected Accounts - Integrations | SERPfinity</title>
        <meta name="description" content="Connect your Google Analytics, Search Console, and advertising accounts to pull live data into your SERPfinity reports." />
      </Helmet>

      <main className="max-w-4xl mx-auto px-4 py-8" role="main" aria-label="Integrations management">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Connected Accounts</h1>
          <p className="mt-2 text-gray-600">
            Connect your data sources to pull live metrics into your reports. Your credentials are encrypted and stored securely.
          </p>
        </header>

        {notification && (
          <div
            role="alert"
            aria-live="polite"
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />}
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto text-current opacity-60 hover:opacity-100" aria-label="Dismiss notification">&times;</button>
          </div>
        )}

        {Object.entries(PROVIDER_CONFIG).map(([key, provider]) => {
          const connectedAccounts = accounts.filter(a => a.provider === key);
          return (
            <section key={key} className="mb-8 bg-white rounded-xl border border-gray-200 shadow-sm" aria-labelledby={`provider-${key}`}>
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0" aria-hidden="true">{provider.icon}</div>
                  <div className="flex-1">
                    <h2 id={`provider-${key}`} className="text-lg font-semibold text-gray-900">{provider.name}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{provider.description}</p>
                  </div>
                  <button
                    onClick={() => handleConnect(key)}
                    disabled={connecting === key}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                    aria-label={`Connect ${provider.name} account`}
                  >
                    {connecting === key ? (
                      <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Link2 className="w-4 h-4" aria-hidden="true" />
                    )}
                    {connecting === key ? 'Connecting...' : connectedAccounts.length > 0 ? 'Add Another' : 'Connect'}
                  </button>
                </div>
              </div>

              {connectedAccounts.length > 0 && (
                <ul className="divide-y divide-gray-100" aria-label={`Connected ${provider.name} accounts`}>
                  {connectedAccounts.map(account => {
                    const statusStyle = STATUS_STYLES[account.status] || STATUS_STYLES.error;
                    const StatusIcon = statusStyle.icon;
                    return (
                      <li key={account.id} className="p-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{account.providerEmail}</p>
                          <p className="text-sm text-gray-500 truncate">{account.providerName}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
                              <StatusIcon className="w-3 h-3" aria-hidden="true" />
                              {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                            </span>
                            {account.lastSyncAt && (
                              <span className="text-xs text-gray-400">Last synced: {new Date(account.lastSyncAt).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDisconnect(account.id, account.providerEmail)}
                          disabled={disconnecting === account.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                          aria-label={`Disconnect ${account.providerEmail}`}
                        >
                          {disconnecting === account.id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                          ) : (
                            <Unlink className="w-3.5 h-3.5" aria-hidden="true" />
                          )}
                          Disconnect
                        </button>
                    </div>

                    {propertyData[account.id] && (
                        <div className="mt-4 pt-4 border-t border-gray-100" >
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Property Settings {getCurrentDomain() && <span className="font-normal text-gray-500">for {getCurrentDomain()}</span>}</h4>
                            {propertyData[account.id]?.error && (<p className="text-xs text-amber-600 mb-3">Unable to load properties. The account may need to be reconnected.</p>)}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Google Analytics 4 Property</label>
                                    <select value={selectedSettings[account.id]?.ga4PropertyId || ''} onChange={(e) => handleSettingChange(account.id, 'ga4PropertyId', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
                                        <option value="">-- Auto-detect --</option>
                                        {(propertyData[account.id]?.properties || []).map(prop => (<option key={prop.propertyId} value={prop.propertyId}>{prop.displayName} ({prop.accountName || prop.propertyId})</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Search Console Site</label>
                                    <select value={selectedSettings[account.id]?.gscSiteUrl || ''} onChange={(e) => handleSettingChange(account.id, 'gscSiteUrl', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
                                        <option value="">-- Auto-detect --</option>
                                        {(propertyData[account.id]?.sites || []).map(site => (<option key={site.siteUrl} value={site.siteUrl}>{site.siteUrl} ({site.permissionLevel})</option>))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-3">
                                <button onClick={() => handleSaveSettings(account.id)} disabled={savingSettings[account.id]} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                    {savingSettings[account.id] ? 'Saving...' : 'Save Settings'}
                                </button>
                                {/* Google Ads account selector (per-user OAuth) */}
                                <div className="mt-4">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Google Ads Account</label>
                                  <div className="flex items-center gap-2">
                                    <select value={selectedSettings[account.id]?.adsCustomerId || ''} onChange={(e) => handleSettingChange(account.id, 'adsCustomerId', e.target.value)} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
                                      <option value="">-- Select Ads account --</option>
                                      {(adsData[account.id]?.customers || []).map(c => (<option key={c.id || c} value={c.id || c}>{c.name ? (c.name + ' (' + (c.id || c) + ')') : (c.id || c)}</option>))}
                                    </select>
                                    <button onClick={() => handleSaveAds(account.id)} disabled={savingAds[account.id]} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                      {savingAds[account.id] ? 'Saving...' : 'Save Ads'}
                                    </button>
                                  </div>
                                  {adsData[account.id]?.error && (<p className="text-xs text-amber-600 mt-1">Unable to load Google Ads accounts. Reconnect Google or check that the Ads scope is granted.</p>)}
                                </div>

                                <span className="text-xs text-gray-500">{selectedSettings[account.id]?.ga4PropertyId || selectedSettings[account.id]?.gscSiteUrl ? 'Custom properties selected' : 'Using automatic domain matching'}</span>
                            </div>
                        </div>
                    )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {connectedAccounts.length === 0 && (
                <div className="p-6 text-center text-gray-400" role="status">
                  <p>No {provider.name} accounts connected yet.</p>
                </div>
              )}

              <div className="px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <Shield className="w-4 h-4" aria-hidden="true" />
                  Data availability
                </h3>
                <dl className="space-y-1">
                  {Object.entries(provider.dataLimits).map(([service, limit]) => (
                    <div key={service} className="flex gap-2 text-sm">
                      <dt className="text-gray-500 font-medium">{service}:</dt>
                      <dd className="text-gray-600">{limit}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </section>
          );
        })}

        <section className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-100" aria-labelledby="coming-soon">
            <h2 id="coming-soon" className="text-lg font-semibold text-blue-900">Coming Soon</h2>
            <p className="text-sm text-blue-700 mt-1">More integrations are on the way to expand your reporting capabilities.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Microsoft Ads (Bing)', category: 'Advertising', description: 'Import Bing Ads campaigns, track keyword performance, and monitor ad spend across the Microsoft Search Network.' },
                { name: 'Meta (Facebook/Instagram)', category: 'Social Media', description: 'Analyze ad campaign performance, audience engagement, and ROI metrics across Facebook and Instagram platforms.' },
                { name: 'LinkedIn Ads', category: 'Advertising', description: 'Track B2B advertising campaigns, monitor lead generation metrics, and measure professional audience engagement.' },
                { name: 'Semrush', category: 'SEO Tools', description: 'Pull keyword rankings, backlink data, competitor analysis, and site audit insights directly into your reports.' },
                { name: 'Ahrefs', category: 'SEO Tools', description: 'Access backlink profiles, organic keyword data, content gap analysis, and domain authority metrics.' },
                { name: 'Moz', category: 'SEO Tools', description: 'Integrate Domain Authority scores, keyword rankings, link metrics, and on-page optimization recommendations.' },
                { name: 'HubSpot', category: 'Marketing Automation', description: 'Connect CRM data with SEO performance to track leads, conversions, and marketing attribution across channels.' },
                { name: 'Mailchimp', category: 'Email Marketing', description: 'Combine email campaign analytics with SEO data to measure cross-channel marketing impact and audience growth.' },
                { name: 'Adobe Analytics', category: 'Analytics', description: 'Import advanced web analytics, custom segments, and multi-channel attribution data for enterprise-level reporting.' }
              ].map(item => (
                <div key={item.name} className="flex flex-col gap-2 px-4 py-4 bg-white rounded-lg border border-blue-100 opacity-70 hover:opacity-90 transition-opacity">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" aria-hidden="true" />
                      <span className="text-sm font-semibold text-gray-800 truncate">{item.name}</span>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 whitespace-nowrap flex-shrink-0">Coming Soon</span>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">{item.category}</span>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </section>
      </main>
    </>
  );
}
